<template>
  <div class="trigger-internal-event-form">
    <div class="trigger-internal-event-form__content">
      <!-- Info banner -->
      <div class="trigger-internal-event-form__info-banner">
        <p>{{ t('flows.operators.triggerInternalEvent.info') }}</p>
      </div>

      <!-- Event selection -->
      <div class="trigger-internal-event-form__field">
        <label class="trigger-internal-event-form__label">
          {{ t('flows.operators.triggerInternalEvent.eventLabel') }}
        </label>
        <div
          class="event-picker-trigger p-dropdown p-component w-full"
          :class="{ 'p-invalid': formValidation?.event?.$errors?.length }"
          role="button"
          tabindex="0"
          @click="eventPickerVisible = true"
          @keydown.enter="eventPickerVisible = true"
          @keydown.space.prevent="eventPickerVisible = true"
        >
          <span
            class="p-dropdown-label p-inputtext"
            :class="{ 'p-placeholder': !localData.event }"
          >
            {{ selectedEventLabel || t('flows.operators.triggerInternalEvent.selectEvent') }}
          </span>
          <div class="p-dropdown-trigger">
            <span class="p-dropdown-trigger-icon pi pi-chevron-down" />
          </div>
        </div>
        <small
          v-if="formValidation?.event?.$errors?.length"
          class="p-error"
        >
          {{ formValidation.event.$errors[0].$message }}
        </small>

        <EventPickerDialog
          v-model:visible="eventPickerVisible"
          v-model="localData.event"
          :predefined-items="predefinedPickerItems"
          :custom-items="customPickerItems"
        />
      </div>

      <!-- Event-specific configuration -->
      <template v-if="localData.event">
        <!-- URL and script info for API events -->
        <template v-if="isApiEvent">
          <div class="trigger-internal-event-form__field">
            <label class="trigger-internal-event-form__label">
              {{ t('flows.operators.triggerInternalEvent.urlLabel') }}
            </label>
            <div class="trigger-internal-event-form__copyable">
              <code>{{ triggerUrl }}</code>
              <Button
                icon="far fa-copy"
                class="p-button-text p-button-sm"
                @click="copyToClipboard(triggerUrl)"
              />
            </div>
          </div>

          <div class="trigger-internal-event-form__field">
            <label class="trigger-internal-event-form__label">
              {{ t('flows.operators.triggerInternalEvent.dataExampleLabel') }}
            </label>
            <pre class="trigger-internal-event-form__code">{{ dataExample }}</pre>
          </div>

          <div class="trigger-internal-event-form__field">
            <label class="trigger-internal-event-form__label">
              {{ t('flows.operators.triggerInternalEvent.scriptLabel') }}
            </label>
            <div class="trigger-internal-event-form__copyable">
              <code>{{ scriptExample }}</code>
              <Button
                icon="far fa-copy"
                class="p-button-text p-button-sm"
                @click="copyToClipboard(scriptExample)"
              />
            </div>
          </div>

          <p class="trigger-internal-event-form__note">
            {{ t('flows.operators.triggerInternalEvent.metadataNote') }}
          </p>
        </template>

        <!-- CustomJS sub-form -->
        <template v-if="localData.event === 'custom_js'">
          <div class="trigger-internal-event-form__field">
            <label class="trigger-internal-event-form__label">
              {{ t('flows.operators.customJS.scriptLabel') }}
            </label>
            <div class="trigger-internal-event-form__custom-js">
              <Textarea
                v-model="localData.token_script"
                rows="4"
                disabled
                class="trigger-internal-event-form__script-textarea"
              />
              <Button
                :label="t('flows.operators.customJS.copyButton')"
                icon="far fa-copy"
                class="p-button-outlined p-button-sm"
                @click="copyToClipboard(localData.token_script || '')"
              />
            </div>
          </div>
        </template>

        <!-- QuitSite sub-form -->
        <template v-if="localData.event === 'quit_site'">
          <div class="trigger-internal-event-form__options">
            <div
              v-for="option in quitSiteOptions"
              :key="option.value"
              class="trigger-internal-event-form__option"
              :class="{ 'trigger-internal-event-form__option--selected': localData.multiplebox_type === option.value }"
              @click="localData.multiplebox_type = option.value"
            >
              <i :class="option.icon" />
              <span>{{ option.label }}</span>
            </div>
          </div>
        </template>

        <!-- ScrollPage sub-form -->
        <template v-if="localData.event === 'scroll'">
          <div class="trigger-internal-event-form__field">
            <label class="trigger-internal-event-form__label">
              {{ t('flows.operators.scrollPage.percentLabel') }}
            </label>
            <div class="trigger-internal-event-form__slider-container">
              <Slider
                v-model="localData.percent_scroll"
                :min="10"
                :max="100"
                :step="10"
                class="trigger-internal-event-form__slider"
              />
              <span class="trigger-internal-event-form__value">{{ localData.percent_scroll }}%</span>
            </div>
          </div>
        </template>

        <!-- SitePassTime sub-form -->
        <template v-if="localData.event === 'pass_time'">
          <div class="trigger-internal-event-form__field">
            <label class="trigger-internal-event-form__label">
              {{ t('flows.operators.sitePassTime.timeElapsed') }}
            </label>
            <div class="trigger-internal-event-form__time-selector">
              <InputNumber
                v-model="timeValue"
                :min="1"
                :max="999"
                class="trigger-internal-event-form__input"
              />
              <Dropdown
                v-model="timeUnit"
                :options="timeUnits"
                option-label="label"
                option-value="value"
                class="trigger-internal-event-form__dropdown"
              />
            </div>
          </div>
          <div class="trigger-internal-event-form__field">
            <label class="trigger-internal-event-form__label">
              {{ t('flows.operators.sitePassTime.period') }}
            </label>
            <div class="trigger-internal-event-form__options">
              <div
                v-for="option in sessionOptions"
                :key="option.value"
                class="trigger-internal-event-form__option"
                :class="{ 'trigger-internal-event-form__option--selected': localData.multiplebox_global_session === option.value }"
                @click="localData.multiplebox_global_session = option.value"
              >
                <i :class="option.icon" />
                <div>
                  <span class="trigger-internal-event-form__option-label">{{ option.label }}</span>
                  <span class="trigger-internal-event-form__option-desc">{{ option.description }}</span>
                </div>
              </div>
            </div>
          </div>
        </template>

        <!-- VisitPage sub-form -->
        <template v-if="localData.event === 'visit_page'">
          <div class="trigger-internal-event-form__field">
            <label class="trigger-internal-event-form__label">
              {{ t('flows.operators.visitPage.titleLabel') }}
            </label>
            <InputText
              v-model="localData.custom_title"
              class="w-full"
              :class="{ 'p-invalid': formValidation?.custom_title?.$errors?.length }"
            />
            <small
              v-if="formValidation?.custom_title?.$errors?.length"
              class="p-error"
            >
              {{ formValidation.custom_title.$errors[0].$message }}
            </small>
          </div>
          <div class="trigger-internal-event-form__field">
            <label class="trigger-internal-event-form__label">
              {{ t('flows.operators.visitPage.descriptionLabel') }}
            </label>
            <InputText
              v-model="localData.custom_description"
              class="w-full"
            />
          </div>
          <div class="trigger-internal-event-form__field">
            <label class="trigger-internal-event-form__label">
              {{ t('flows.operators.visitPage.triggerLocation') }}
            </label>
            <div class="trigger-internal-event-form__options">
              <div
                v-for="option in locationOptions"
                :key="option.value"
                class="trigger-internal-event-form__option"
                :class="{ 'trigger-internal-event-form__option--selected': localData.trigger_location === option.value }"
                @click="localData.trigger_location = option.value"
              >
                <i :class="option.icon" />
                <div>
                  <span class="trigger-internal-event-form__option-label">{{ option.label }}</span>
                  <span class="trigger-internal-event-form__option-desc">{{ option.description }}</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Navigation criteria panel -->
          <FilterPanelContent
            v-if="localData.trigger_location === 0"
            :active-items="activeItemsFilterPanel"
            :panels="['browsing_criteria']"
          >
            <template #browsing_criteria>
              <FilterSegmentForm
                :segments="segments"
                :active-segments="activeSegments"
                :items="filters"
                :current-step-id="operatorId"
                segment-label="automatedScenarios.fields.segments.browsingCriteriaLabel"
                placeholder-select-segments="automatedScenarios.fields.segments.browsingCriteria"
                @add:segment="handleSaveSegment"
                @remove:segment="({ segmentName, number }) => handleRemoveSegment(segmentName, number)"
              />
            </template>
          </FilterPanelContent>
        </template>

        <!-- Segmentation of the trigger (visit_page, custom_js), off until switched on -->
        <div
          v-if="showSegmentation"
          class="trigger-internal-event-form__field trigger-internal-event-form__segmentation"
          :class="{ 'trigger-internal-event-form__segmentation--enabled': segmentationEnabled }"
        >
          <div class="trigger-internal-event-form__switch-row">
            <label
              for="triggerSegmentationEnabled"
              class="trigger-internal-event-form__switch-label"
            >
              <span class="trigger-internal-event-form__option-label">
                {{ t('flows.operators.triggerInternalEvent.segmentation.label') }}
              </span>
              <span class="trigger-internal-event-form__option-desc">
                {{ segmentationSwitchDescription }}
              </span>
            </label>
            <InputSwitch
              v-model="segmentationEnabled"
              input-id="triggerSegmentationEnabled"
            />
          </div>
          <template v-if="segmentationEnabled">
            <p class="trigger-internal-event-form__note trigger-internal-event-form__segmentation-note">
              {{ t('flows.operators.triggerInternalEvent.segmentation.info') }}
            </p>
            <SegmentationsPanel
              :model-value="localData.segmentation_config"
              :origin="triggerSegmentationOrigin"
              size="small"
              @update:model-value="onSegmentationChange"
            />
            <small
              v-if="formValidation?.segmentation_config?.$errors?.length"
              class="p-error"
            >
              {{ formValidation.segmentation_config.$errors[0].$message }}
            </small>
          </template>
        </div>
      </template>
    </div>
  </div>
</template>

<script lang="ts">
import {
  computed, defineAsyncComponent, defineComponent, inject, onBeforeMount, PropType, ref, Ref, watch,
} from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'primevue/button';
import Dropdown from 'primevue/dropdown';
import Slider from 'primevue/slider';
import InputNumber from 'primevue/inputnumber';
import InputText from 'primevue/inputtext';
import InputSwitch from 'primevue/inputswitch';
import Textarea from 'primevue/textarea';
import { FilterSegmentForm, FilterPanelContent } from '@/components/flows/operators/shared';
import { removeInSegment, addInSegment } from '@/composables/flows/useFlowSegments';
import type { ActiveSegment } from '@/composables/flows/useFlowSegments';
import type { FilterSegment } from '@/types/flow-types';
// eslint-disable-next-line import/no-cycle
import getSegments from '@/components/flows/operators/triggers/VisitPage/segments/index';
import { NestApiType, nestCall } from '@/composables/nestApi';
import { eventsTypeEnum, SortOrderEnum } from '@/types';
import { OperatorType } from '@/types/generated-types/graphql';
import { predefinedEvents } from '@/composables/events/constants';
import type { Event } from '@/types/events-types';
import { ensureTokenScript, normalizeTokenScript } from '@/components/flows/operators/triggers/CustomJS';
import {
  hasTriggerSegmentation,
  isSegmentableTriggerEvent,
  parseTriggerSegmentation,
  TRIGGER_SEGMENTATION_ORIGIN,
} from '@/composables/segmentations/triggerSegmentation';
import type { TriggerInternalEventData, VisitPageFilters } from './index';
import EventPickerDialog from './EventPickerDialog.vue';
import type { EventPickerItem } from './EventPickerDialog.vue';

export default defineComponent({
  name: 'TriggerInternalEventForm',

  components: {
    Button,
    Dropdown,
    Slider,
    InputNumber,
    InputText,
    InputSwitch,
    Textarea,
    FilterPanelContent,
    FilterSegmentForm,
    EventPickerDialog,
    SegmentationsPanel: defineAsyncComponent(() => import('@/components/segmentations/SegmentationsPanel.vue')),
  },

  props: {
    modelValue: {
      type: Object as PropType<TriggerInternalEventData>,
      default: () => ({ event: '' }),
    },

    formValidation: {
      type: Object,
      default: () => ({}),
    },

    operatorId: {
      type: String,
      default: null,
    },
  },

  emits: ['update:modelValue', 'cancel', 'close', 'submit'],

  setup(props, { emit }) {
    const { t } = useI18n();

    const localData = ref<TriggerInternalEventData>({
      event: props.modelValue.event ?? '',
      webhook: props.modelValue.webhook ?? 0,
      multiplebox_type: props.modelValue.multiplebox_type ?? '0',
      percent_scroll: Number(props.modelValue.percent_scroll ?? 50),
      time_unit_select: props.modelValue.time_unit_select ?? { unit: 'MINUTE', value: 10 },
      multiplebox_global_session: props.modelValue.multiplebox_global_session ?? 'global',
      custom_title: props.modelValue.custom_title ?? '',
      custom_description: props.modelValue.custom_description ?? '',
      trigger_location: Number(props.modelValue.trigger_location ?? 1),
      token_script: props.modelValue.token_script ?? '',
      segmentation_config: parseTriggerSegmentation(props.modelValue.segmentation_config),
    });

    // Shown on « Visite une page » and « Déclencheur personnalisé », for every shop
    const hasSavedSegmentation = hasTriggerSegmentation(props.modelValue.segmentation_config);
    const showSegmentation = computed(() => isSegmentableTriggerEvent(localData.value.event));

    // Off by default: the conditions are only configured once switched on.
    // A trigger saved with a segmentation opens switched on.
    const segmentationEnabled = ref(hasSavedSegmentation);

    // Conditions set aside when switched off, given back when switched on again
    let setAsideSegmentation: unknown[] = [];

    watch(segmentationEnabled, (enabled) => {
      if (enabled) {
        localData.value.segmentation_config = setAsideSegmentation;
        return;
      }
      setAsideSegmentation = JSON.parse(JSON.stringify(localData.value.segmentation_config ?? []));
      // An emptied panel removes the saved segmentation on save; an empty
      // list leaves a trigger that had none as it was.
      localData.value.segmentation_config = hasSavedSegmentation ? [[{}]] : [];
    });

    const segmentationSwitchDescription = computed(() => {
      if (segmentationEnabled.value) {
        return t('flows.operators.triggerInternalEvent.segmentation.switchOn');
      }
      return hasSavedSegmentation
        ? t('flows.operators.triggerInternalEvent.segmentation.switchOffSaved')
        : t('flows.operators.triggerInternalEvent.segmentation.switchOff');
    });

    // A panel without condition saves nothing new; a segmentation saved
    // before is kept as emptied so that saving removes it. The panel being
    // closed when switched off, its last emission is ignored.
    const onSegmentationChange = (config: unknown[]) => {
      if (!segmentationEnabled.value) {
        return;
      }
      localData.value.segmentation_config = hasSavedSegmentation || hasTriggerSegmentation(config) ? config : [];
    };

    const originalEvent = ref(props.modelValue.event);
    const originalWebhook = ref(props.modelValue.webhook);

    const timeValue = computed({
      get: () => localData.value.time_unit_select?.value ?? 10,
      set: (val) => {
        localData.value.time_unit_select = {
          ...localData.value.time_unit_select!,
          value: val,
        };
      },
    });

    const timeUnit = computed({
      get: () => localData.value.time_unit_select?.unit ?? 'MINUTE',
      set: (val) => {
        localData.value.time_unit_select = {
          ...localData.value.time_unit_select!,
          unit: val,
        };
      },
    });

    // Events grouped by type (predefined vs custom)
    const availableEventsRaw = ref<{ label: string; code: string; items: { label: string; value: string }[] }[]>([
      {
        label: t('events.dropdownGroups.predefined'),
        code: 'DEF',
        items: [],
      },
      {
        label: t('events.dropdownGroups.custom'),
        code: 'CUSTOM',
        items: [],
      },
    ]);
    const availableEvents = computed(() => availableEventsRaw.value.filter((g) => g.items.length > 0));

    // All events loaded from API (for building payload examples)
    const allEvents = ref<Event[]>([]);

    // ============================================================
    // Modal event picker (replaces the legacy <Dropdown> dropdown)
    // ============================================================
    const eventPickerVisible = ref(false);

    // Predefined items — flat list for the picker. Built from the same
    // `predefinedEvents` source as availableEventsRaw, but without the
    // grouping wrapper since the picker handles grouping itself.
    // Events incompatible as a child of the insertion parent (provided by the
    // operator panel). Hidden from the picker so the user can only pick a valid
    // event. Empty in edit mode / when there is no parent.
    const forbiddenEventValues = inject<Ref<string[]>>('forbiddenEventValues', ref<string[]>([]));

    const predefinedPickerItems = computed<EventPickerItem[]>(() => {
      const forbidden = forbiddenEventValues.value;
      const items: EventPickerItem[] = [];
      Object.keys(predefinedEvents)
        .filter((key) => predefinedEvents[key].display && !forbidden.includes(predefinedEvents[key].code_name))
        .forEach((key) => {
          items.push({
            label: t(predefinedEvents[key].label),
            value: predefinedEvents[key].code_name,
          });
        });
      return items.sort((a, b) => a.label.localeCompare(b.label));
    });

    // Custom items — events fetched from API (`allEvents`). Each carries
    // `owner_integration` when created via a partner API key; the picker
    // uses that to bucket items into per-integration groups.
    const customPickerItems = computed<EventPickerItem[]>(() => {
      const items: EventPickerItem[] = [];
      for (const ev of allEvents.value) {
        // Skip events that are also in predefinedEvents (deduplication).
        if (Object.keys(predefinedEvents).includes(ev.code_name)) continue;
        items.push({
          label: ev.name,
          value: ev.code_name,
          owner_integration: (ev as any).owner_integration ?? null,
        });
      }
      return items.sort((a, b) => a.label.localeCompare(b.label));
    });

    // Label rendered inside the trigger button (mimics what a Dropdown
    // would show as `option-label` once an option is selected).
    const selectedEventLabel = computed(() => {
      const code = localData.value.event;
      if (!code) return '';
      const predef = predefinedPickerItems.value.find((i) => i.value === code);
      if (predef) return predef.label;
      const custom = customPickerItems.value.find((i) => i.value === code);
      if (custom) return custom.label;
      return code;
    });

    // Check if selected event is a custom event (shows URL/script info)
    const isCustomEvent = computed(() => {
      const eventCode = localData.value.event;
      if (!eventCode) return false;
      // Custom events are those not in predefinedEvents
      return !Object.keys(predefinedEvents).includes(eventCode);
    });

    // Check if selected event is a predefined event with persistant flag (also shows URL/script)
    const isPersistantEvent = computed(() => {
      const eventCode = localData.value.event;
      if (!eventCode) return false;
      return Object.keys(predefinedEvents).includes(eventCode) && predefinedEvents[eventCode].persistant === true;
    });

    // Show API info for custom events or persistant predefined events
    const isApiEvent = computed(() => isCustomEvent.value || isPersistantEvent.value);

    const triggerUrl = computed(() => `${process.env.VUE_APP_NESTJS_CLIENT_API_URL}/${process.env.VUE_APP_NESTJS_CLIENT_API_VERSION}/events/trigger/${localData.value.event}`);

    // Build payload example from event properties
    const buildPayloadExample = (properties: any): any => {
      if (!properties) return {};

      const output: any = {};

      if (properties.metaData && Array.isArray(properties.metaData)) {
        output.metaData = properties.metaData.reduce((acc: any, item: any) => {
          if (item && typeof item === 'object' && 'name' in item) {
            let value: any;
            switch (item.type) {
              case 'bool':
                value = true;
                break;
              case 'text':
              case 'longtext':
                value = 'text';
                break;
              case 'number':
                value = 1;
                break;
              case 'decimal':
                value = 1.23;
                break;
              case 'date':
                value = new Date().toISOString().split('T')[0];
                break;
              case 'json':
                value = '[{"key1": "value1", "key2": "value2"}]';
                break;
              default:
                value = null;
            }
            acc[item.name] = value;
          }
          return acc;
        }, {});
      }

      if (properties.contact && Object.keys(properties.contact).length > 0) {
        output.contact = { email: 'example@example.com' };
      }

      if (properties.visitor && Object.keys(properties.visitor).length > 0) {
        output.visitor = { id: 1 };
      }

      return output;
    };

    const dataExample = computed(() => {
      const eventCode = localData.value.event;
      if (!eventCode) return '';

      // Check if it's a custom event loaded from API
      const customEvent = allEvents.value.find((e) => e.code_name === eventCode);
      if (customEvent?.properties) {
        return JSON.stringify(buildPayloadExample(customEvent.properties), null, 2);
      }

      // Fallback for predefined events
      const predefined = predefinedEvents[eventCode];
      if (predefined?.properties) {
        return JSON.stringify(buildPayloadExample(predefined.properties), null, 2);
      }

      return '{}';
    });

    const scriptExample = computed(() => {
      const eventCode = localData.value.event;
      if (!eventCode) return '';

      // Get metaData from the payload example
      const customEvent = allEvents.value.find((e) => e.code_name === eventCode);
      const properties = customEvent?.properties || predefinedEvents[eventCode]?.properties;

      if (properties) {
        const payload = buildPayloadExample(properties);
        const metaData = payload.metaData || {};
        return `shopimind.executeEvent('${eventCode}', ${JSON.stringify(metaData)});`;
      }

      return `shopimind.executeEvent('${eventCode}', {});`;
    });

    const copyToClipboard = (text: string) => {
      navigator.clipboard.writeText(text);
    };

    const quitSiteOptions = [
      { value: '0', label: t('flows.operators.quitSite.hasQuit'), icon: 'far fa-sign-out' },
      { value: '1', label: t('flows.operators.quitSite.isGoingToQuit'), icon: 'far fa-hand' },
    ];

    const timeUnits = [
      { value: 'SECOND', label: t('flows.operators.sitePassTime.seconds') },
      { value: 'MINUTE', label: t('flows.operators.sitePassTime.minutes') },
      { value: 'HOUR', label: t('flows.operators.sitePassTime.hours') },
    ];

    const sessionOptions = [
      {
        value: 'global',
        label: t('flows.operators.sitePassTime.global'),
        description: t('flows.operators.sitePassTime.globalDesc'),
        icon: 'far fa-globe',
      },
      {
        value: 'session',
        label: t('flows.operators.sitePassTime.session'),
        description: t('flows.operators.sitePassTime.sessionDesc'),
        icon: 'far fa-window',
      },
    ];

    const locationOptions = [
      {
        value: 1,
        label: t('flows.operators.visitPage.allPages'),
        description: t('flows.operators.visitPage.allPagesDesc'),
        icon: 'far fa-globe',
      },
      {
        value: 0,
        label: t('flows.operators.visitPage.specificPages'),
        description: t('flows.operators.visitPage.specificPagesDesc'),
        icon: 'far fa-filter',
      },
    ];

    // VisitPage segments/filters
    const filters: Ref<VisitPageFilters> = ref({
      products: props.modelValue.products ?? {},
      categories: props.modelValue.categories ?? {},
      manufacturers: props.modelValue.manufacturers ?? {},
      urls: props.modelValue.urls ?? {},
    });

    const segments: Ref<FilterSegment<VisitPageFilters>[]> = ref([]);

    // Load segments on mount
    segments.value = getSegments();

    // Build activeSegments manually (no `number`) so FilterSegmentForm.editSegment
    // reads flat data — matches how addInSegment stores segments here.
    const activeSegments = computed<ActiveSegment[]>(() => {
      const active: ActiveSegment[] = [];
      (['products', 'categories', 'manufacturers', 'urls'] as (keyof VisitPageFilters)[]).forEach((id) => {
        const filter = filters.value[id];
        if (filter && Object.keys(filter).length > 0) {
          const segment = segments.value.find((s) => s.id === id);
          if (segment) {
            active.push({
              name: id,
              label: segment.option.label,
              show: segment.fields.some((field) => !field.hidden),
            });
          }
        }
      });
      return active;
    });

    const activeItemsFilterPanel = computed(() => ({ browsing_criteria: activeSegments.value.length }));

    const updateFilters = () => {
      localData.value = {
        ...localData.value,
        products: filters.value.products,
        categories: filters.value.categories,
        manufacturers: filters.value.manufacturers,
        urls: filters.value.urls,
      };
    };

    const handleSaveSegment = (segment: FilterSegment<VisitPageFilters>) => {
      const currentFilter = filters.value[segment.id as keyof VisitPageFilters];
      // Pass undefined if filter is empty object, so addInArraySegment initializes segment_number correctly
      const filterToPass = (currentFilter && Object.keys(currentFilter).length > 0) ? currentFilter : undefined;
      filters.value = {
        ...filters.value,
        [segment.id]: { ...addInSegment<VisitPageFilters>(segment, filterToPass) },
      };
      updateFilters();
    };

    const handleRemoveSegment = (segmentName: keyof VisitPageFilters, number?: number) => {
      filters.value = removeInSegment<VisitPageFilters>(filters.value, segmentName, number ?? 0);
      updateFilters();
    };

    // Load events on mount
    onBeforeMount(async () => {
      // Load predefined events
      Object.keys(predefinedEvents)
        .filter((eventKey) => predefinedEvents[eventKey].display)
        .forEach((key: string) => {
          const defGroup = availableEventsRaw.value.find((group) => group.code === 'DEF');
          if (defGroup) {
            defGroup.items.push({
              label: t(predefinedEvents[key].label),
              value: predefinedEvents[key].code_name,
            });
          }
        });

      // Sort predefined events alphabetically
      const defGroup = availableEventsRaw.value.find((group) => group.code === 'DEF');
      if (defGroup) {
        defGroup.items.sort((a, b) => a.label.localeCompare(b.label));
      }

      // Fetch custom events from API
      const params = {
        limit: 100,
        offset: 0,
        fetchAll: true,
        order: [
          {
            field: 'name',
            type: (SortOrderEnum.ASC as string).toLowerCase(),
          },
        ],
        filters: [
          {
            field: 'type',
            value: eventsTypeEnum.EXTERNAL,
            operator: OperatorType.Equals,
          },
        ],
      };

      try {
        const events = await nestCall('get', NestApiType.CLIENT_API, '/events', {}, '', undefined, true, params);

        if (events?.data) {
          allEvents.value = events.data;

          const customGroup = availableEventsRaw.value.find((group) => group.code === 'CUSTOM');
          if (customGroup) {
            (events.data ?? []).forEach((result: Event) => {
              // Only add if not already a predefined event
              if (!Object.keys(predefinedEvents).includes(result.code_name)) {
                customGroup.items.push({
                  label: result.name,
                  value: result.code_name,
                });
              }
            });

            // Sort custom events alphabetically
            customGroup.items.sort((a, b) => a.label.localeCompare(b.label));
          }
        }
      } catch (error) {
        console.error('Failed to load custom events:', error);
      }
    });

    watch(localData, (newData) => {
      emit('update:modelValue', newData);
    }, { deep: true });

    watch(() => props.modelValue, (newValue) => {
      if (newValue !== localData.value) {
        localData.value = {
          ...localData.value,
          ...newValue,
          // Ensure numeric values are numbers for strict equality and toFixed
          percent_scroll: Number(newValue.percent_scroll ?? 50),
          trigger_location: Number(newValue.trigger_location ?? 1),
          token_script: normalizeTokenScript(newValue.token_script ?? ''),
          segmentation_config: parseTriggerSegmentation(newValue.segmentation_config),
        };
        // Update filters from modelValue
        filters.value = {
          products: newValue.products ?? {},
          categories: newValue.categories ?? {},
          manufacturers: newValue.manufacturers ?? {},
          urls: newValue.urls ?? {},
        };
      }
    });

    // Track previous event to detect actual changes
    let previousEvent = '';

    watch(() => localData.value.event, (newEvent) => {
      if (previousEvent && newEvent !== previousEvent) {
        // User is changing events - reset fields for the new event to defaults
        switch (newEvent) {
          case 'quit_site':
            localData.value.multiplebox_type = '0';
            break;
          case 'scroll':
            localData.value.percent_scroll = 50;
            break;
          case 'pass_time':
            localData.value.time_unit_select = { unit: 'MINUTE', value: 10 };
            localData.value.multiplebox_global_session = 'global';
            break;
          case 'visit_page':
            localData.value.custom_title = t('flows.operators.visitPage.label');
            localData.value.custom_description = '';
            localData.value.trigger_location = 1;
            break;
          case 'custom_js':
            localData.value.token_script = ensureTokenScript(localData.value.token_script);
            break;
          default:
            // For other events (custom, upsert_order, etc.), no specific fields to reset
            break;
        }

        // Reset webhook to original value if same event
        if (newEvent === originalEvent.value) {
          localData.value.webhook = originalWebhook.value;
        } else {
          // Reset webhook to create a new one if the new event is different from the original one
          localData.value.webhook = 0;
        }

        // Clear custom_title for non-visit_page events so callback regenerates it on save
        if (newEvent !== 'visit_page') {
          localData.value.custom_title = '';
        }

        // Handle customPersistance based on event's persistant flag
        if (predefinedEvents[newEvent]?.persistant) {
          (localData.value as any).customPersistance = {
            boxtriggerinternalevent: ['{CURRENT_OPERATOR_ID}'],
          };
        } else {
          // Clear customPersistance if the event is not persistant
          delete (localData.value as any).customPersistance;
        }

        // A segmentation not saved yet goes away with its event
        if (!isSegmentableTriggerEvent(newEvent) && !hasSavedSegmentation) {
          localData.value.segmentation_config = [];
          segmentationEnabled.value = false;
        }
      } else if (newEvent === 'visit_page' && !localData.value.custom_title) {
        // Initial load for visit_page with no custom_title
        localData.value.custom_title = t('flows.operators.visitPage.label');
      } else if (newEvent === 'custom_js') {
        // Initial selection or load — generate the script, or repair a saved one so the repair is emitted
        localData.value.token_script = ensureTokenScript(localData.value.token_script);
      }

      previousEvent = newEvent;
    }, { immediate: true });

    return {
      t,
      localData,
      availableEvents,
      isApiEvent,
      triggerUrl,
      dataExample,
      scriptExample,
      copyToClipboard,
      quitSiteOptions,
      timeValue,
      timeUnit,
      timeUnits,
      sessionOptions,
      locationOptions,
      // Segments
      filters,
      segments,
      activeSegments,
      activeItemsFilterPanel,
      handleSaveSegment,
      handleRemoveSegment,
      // Event picker
      eventPickerVisible,
      predefinedPickerItems,
      customPickerItems,
      selectedEventLabel,
      // Segmentation
      showSegmentation,
      segmentationEnabled,
      segmentationSwitchDescription,
      onSegmentationChange,
      triggerSegmentationOrigin: TRIGGER_SEGMENTATION_ORIGIN,
    };
  },
});
</script>

<style lang="scss" scoped>
.trigger-internal-event-form {
  display: flex;
  flex-direction: column;
  height: 100%;

  &__content {
    flex: 1;
    padding: 1rem;
    overflow-y: auto;
  }

  &__info-banner {
    padding: 0.75rem 1rem;
    background: #e0f2fe;
    border-left: 4px solid #0ea5e9;
    border-radius: 0.25rem;
    margin-bottom: 1.5rem;

    p {
      margin: 0;
      font-size: 0.875rem;
      color: #0369a1;
    }
  }

  &__field {
    margin-bottom: 1.5rem;
  }

  &__label {
    display: block;
    margin-bottom: 0.5rem;
    font-size: 0.875rem;
    font-weight: 500;
    color: #374151;
  }

  &__copyable {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem 0.75rem;
    background: #f3f4f6;
    border-radius: 0.375rem;
    overflow-x: auto;

    code {
      flex: 1;
      font-size: 0.75rem;
      color: #374151;
      white-space: nowrap;
    }

    .p-button {
      flex-shrink: 0;
    }
  }

  &__code {
    padding: 0.75rem;
    background: #f3f4f6;
    border-radius: 0.375rem;
    font-size: 0.75rem;
    color: #374151;
    overflow-x: auto;
    margin: 0;
  }

  &__note {
    font-size: 0.75rem;
    color: #6b7280;
    font-style: italic;
    margin: 0 0 1.5rem 0;
  }

  &__options {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  &__option {
    display: flex;
    align-items: flex-start;
    gap: 0.75rem;
    padding: 0.75rem 1rem;
    border: 1px solid #e5e7eb;
    border-radius: 0.5rem;
    cursor: pointer;
    transition: all 0.15s ease;

    i {
      font-size: 1rem;
      color: #6b7280;
      margin-top: 0.125rem;
    }

    &:hover {
      border-color: $brand-color-primary;
      background: rgba($brand-color-primary, 0.05);
    }

    &--selected {
      border-color: $brand-color-primary;
      background: rgba($brand-color-primary, 0.1);

      i {
        color: $brand-color-primary;
      }
    }
  }

  &__option-label {
    display: block;
    font-size: 0.875rem;
    font-weight: 500;
    color: #374151;
  }

  &__option-desc {
    display: block;
    font-size: 0.75rem;
    color: #6b7280;
    margin-top: 0.125rem;
  }

  &__slider-container {
    display: flex;
    align-items: center;
    gap: 1rem;
  }

  &__slider {
    flex: 1;
  }

  &__value {
    min-width: 3rem;
    font-size: 1rem;
    font-weight: 600;
    color: $brand-color-primary;
    text-align: right;
  }

  &__time-selector {
    display: flex;
    gap: 0.5rem;
  }

  &__input {
    width: 100px;
  }

  &__dropdown {
    flex: 1;
  }

  &__segments {
    padding: 1rem;
    background: #fef3c7;
    border-radius: 0.5rem;
    margin-top: 1rem;
  }

  &__segments-info {
    margin: 0;
    font-size: 0.875rem;
    color: #92400e;
  }

  &__custom-js {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  &__segmentation {
    padding: 0.75rem 1rem;
    border: 1px solid #e5e7eb;
    border-radius: 0.5rem;
    transition: border-color 0.15s ease;

    &--enabled {
      border-color: $brand-color-primary;
    }
  }

  &__switch-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;

    :deep(.p-inputswitch) {
      flex-shrink: 0;
    }
  }

  &__switch-label {
    margin: 0;
    cursor: pointer;
  }

  &__segmentation-note {
    margin: 0.75rem 0;
  }

  &__script-textarea {
    resize: none !important;
    min-height: 140px;
    font-family: monospace;
    font-size: 0.8rem;
  }
}

/* Trigger button that opens the EventPickerDialog. Reuses the .p-dropdown
   classes so it visually inherits the PrimeVue Dropdown theme without
   actually rendering a Dropdown. */
.event-picker-trigger {
  cursor: pointer;
  user-select: none;
  display: flex;
  align-items: center;

  &:focus-visible {
    outline: 2px solid $brand-color-primary;
    outline-offset: 1px;
  }
}
</style>
